import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import type {
  BetActivityItem,
  BetActivityData,
  DailyBetTask,
} from "./BetActivity.types";
import { useUserStore } from "@/modules/user/stores";

// ==================== 弹窗显示判定 ====================

/**
 * 检查是否有 day1 可领取（用于弹窗打开时直接显示领取弹窗）
 * day1 的 canClaim = true && claimed = false
 *
 * 注意：本函数原本定义在 BetActivity.tsx，迁移到 utils 是为了让 index.ts 不再
 * 通过 re-export 把 BetActivity.tsx（含 .scss）拖进 main chunk，从而修复
 * 生产构建下 BetActivity.scss 与 Dialog.scss 的 CSS 顺序冲突问题。
 */
export const hasClaimableDay1Rewards = (
  dailyBetTask: DailyBetTask | undefined | null
): boolean => {
  if (!dailyBetTask?.betData) return false;

  const { taskList, currentBetAmount = 0 } = dailyBetTask.betData;
  if (!taskList?.length) return false;

  return taskList.some(task => {
    if (task.betAmount > currentBetAmount) return false;
    const day1Rule = task.releaseRuleItem?.find(r => r.day === 1);
    return day1Rule?.canClaim === true && day1Rule?.claimed !== true;
  });
};

// ==================== 金额计算工具函数 ====================

/**
 * 精确乘法（避免浮点数精度问题）
 * 原理：将浮点数转换为整数运算，避免精度丢失
 * @param num1 被乘数
 * @param num2 乘数
 * @returns 精确的乘积（四舍五入到2位小数）
 */
export const preciseMultiply = (num1: number, num2: number): number => {
  const num1Str = num1.toString();
  const num2Str = num2.toString();
  const num1Decimals = (num1Str.split(".")[1] || "").length;
  const num2Decimals = (num2Str.split(".")[1] || "").length;
  const totalDecimals = num1Decimals + num2Decimals;
  const int1 = Number(num1Str.replace(".", ""));
  const int2 = Number(num2Str.replace(".", ""));
  return Math.round(((int1 * int2) / Math.pow(10, totalDecimals)) * 100) / 100;
};

/**
 * 格式化金额显示（根据实际小数位数显示，最多2位）
 * @param amount 金额
 * @returns 格式化后的字符串（自动添加千分位，移除尾部0）
 */
export const formatAmount = (amount: number): string => {
  const rounded = Math.round(amount * 100) / 100;
  const str = rounded.toFixed(2);
  const trimmed = str.replace(/\.?0+$/, "");
  const [intPart, decimalPart] = trimmed.split(".");
  const formattedInt = Number(intPart).toLocaleString();
  return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
};

// ==================== 常量 ====================

/** 进度条动画持续时间（毫秒） */
export const PROGRESS_ANIMATION_DURATION = 1200;

/**
 * 进度条缓动函数：cubic-bezier(0.25, 0.1, 0.25, 1)
 * 给定进度位置比例（0-1），返回对应的时间比例（0-1）
 * 这个函数用于匹配 CSS 的 cubic-bezier，让节点解锁时间与进度条速度同步
 */
const easeInOutCubic = (t: number): number => {
  // 简化版的 cubic-bezier(0.25, 0.1, 0.25, 1) 近似
  // 这个曲线在结束时会减速
  return t < 0.5
    ? 4 * t * t * t // 前半段加速
    : 1 - Math.pow(-2 * t + 2, 3) / 2; // 后半段减速
};

/**
 * 逆向缓动函数：给定位置比例（0-1），返回时间比例（0-1）
 * 用于计算进度条到达某个位置需要多少时间
 */
const inverseEasing = (position: number): number => {
  // 对于 cubic-bezier 曲线，我们需要找到 easeInOutCubic(t) = position 的 t 值
  // 使用数值方法（二分查找）来求解
  if (position <= 0) return 0;
  if (position >= 1) return 1;

  let low = 0;
  let high = 1;
  let mid = 0.5;
  const epsilon = 0.001; // 精度

  // 二分查找
  for (let i = 0; i < 20; i++) {
    mid = (low + high) / 2;
    const value = easeInOutCubic(mid);
    if (Math.abs(value - position) < epsilon) {
      break;
    }
    if (value < position) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return mid;
};

/** 卡片高度 (rem) - 54px = 3.375rem */
export const CARD_HEIGHT = 3.375;

/** 卡片间距 (rem) - 8px = 0.5rem */
export const CARD_GAP = 0.5;

/** 卡片高度 + 间距 (rem) */
export const ITEM_HEIGHT = CARD_HEIGHT + CARD_GAP;

// ==================== 倒计时工具函数 ====================

/**
 * 格式化倒计时
 */
export const formatCountdown = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

/**
 * ✅ 计算普通 bet 活动的倒计时（基于服务器返回的秒数，和bet加速倒计时处理方式一致）
 * @param betActivityEndTime 活动结束时间戳（毫秒）
 */
export const calculateBetActivityCountdown = (
  betActivityEndTime: number | undefined
) => {
  if (betActivityEndTime) {
    const remaining = betActivityEndTime - Date.now();
    return formatCountdown(remaining > 0 ? remaining : 0);
  }

  return { hours: "00", minutes: "00", seconds: "00" };
};

/**
 * 计算 bet 加速模式的倒计时
 * @param doubleBetEndTime 加速模式结束时间戳（毫秒）
 */
export const calculateDoubleBetCountdown = (
  doubleBetEndTime: number | undefined
) => {
  if (doubleBetEndTime) {
    const remaining = doubleBetEndTime - Date.now();
    return formatCountdown(remaining > 0 ? remaining : 0);
  }

  return { hours: "00", minutes: "00", seconds: "00" };
};

// ==================== 进度条计算工具函数 ====================

/**
 * 计算进度条目标高度（rem）
 * 基于 currentBetAmount 占各档位金额的比例来计算
 */
export const calculateProgressHeight = (
  currentAmount: number,
  items: BetActivityItem[]
): number => {
  if (currentAmount <= 0 || items.length === 0) {
    return 0.625; // 10px = 0.625rem (最小高度)
  }

  // 找到当前金额落在哪个节点区间（已达到的最大节点索引）
  let currentIndex = -1;
  for (let i = 0; i < items.length; i++) {
    if (currentAmount >= items[i].betAmount) {
      currentIndex = i;
    } else {
      break;
    }
  }

  // 如果还没达到第一个节点，按金额比例计算到第一个节点的进度
  if (currentIndex === -1) {
    const firstBetAmount = items[0].betAmount;
    const amountRatio = currentAmount / firstBetAmount; // 金额占比
    const firstNodeCenter = CARD_HEIGHT / 2; // 第一档节点中心位置
    if (amountRatio < 0.2) {
      return 0.625; // 最小高度 10px = 0.625rem
    }
    return amountRatio * firstNodeCenter;
  }

  // 计算当前已达到节点的中心位置
  const currentNodeCenter = currentIndex * ITEM_HEIGHT + CARD_HEIGHT / 2;

  // 如果已经达到或超过最后一个节点，继续往下延伸
  if (currentIndex === items.length - 1) {
    const lastBetAmount = items[currentIndex].betAmount;
    if (currentAmount > lastBetAmount) {
      const extraHeight = CARD_HEIGHT / 2;
      const overAmount = currentAmount - lastBetAmount;
      const overRatio = Math.min(overAmount / (lastBetAmount * 0.5), 1);
      const finalHeight = currentNodeCenter + overRatio * extraHeight;
      return finalHeight;
    }
    return currentNodeCenter;
  }

  // ✅ 在两个节点之间根据 betAmount 按金额占比线性插值
  const currentBetAmount = items[currentIndex].betAmount;
  const nextBetAmount = items[currentIndex + 1].betAmount;

  // 计算金额在两档之间的占比
  const amountRatio =
    (currentAmount - currentBetAmount) / (nextBetAmount - currentBetAmount);

  // 计算下一节点中心位置
  const nextNodeCenter = (currentIndex + 1) * ITEM_HEIGHT + CARD_HEIGHT / 2;

  // 根据金额占比在两个节点中心之间插值
  const progressHeight =
    currentNodeCenter + amountRatio * (nextNodeCenter - currentNodeCenter);
  return progressHeight;
};

/**
 * 计算节点位置（rem）
 * 用于定位进度节点图标
 */
export const getNodePosition = (index: number): number => {
  const nodeHeight = 1.3125; // 21px = 1.3125rem
  return index * ITEM_HEIGHT + CARD_HEIGHT / 2 - nodeHeight / 2;
};

/**
 * 计算节点中心位置（rem）
 */
export const getNodeCenter = (index: number): number => {
  return index * ITEM_HEIGHT + CARD_HEIGHT / 2;
};

// ==================== 自定义 Hooks ====================

/**
 * 明天0点倒计时 Hook
 */
/**
 * ✅ 明天0点倒计时 hook（使用服务器数据 + 缓存逻辑）
 * @param tomorrowEndTime 明天0点的结束时间戳（从 getTomorrowEndTimeFromSeconds 计算得到）
 * @returns 格式化的倒计时字符串 HH:MM:SS
 */
export const useTomorrowCountdown = (tomorrowEndTime?: number) => {
  const [countdown, setCountdown] = useState("00:00:00");

  useEffect(() => {
    // 如果没有传入结束时间，使用客户端计算的明天0点
    const getDefaultTomorrowTime = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setHours(24, 0, 0, 0);
      return tomorrow.getTime();
    };

    const targetTime = tomorrowEndTime || getDefaultTomorrowTime();

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = targetTime - now;

      if (remaining > 0) {
        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor(
          (remaining % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

        setCountdown(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
        );
      } else {
        setCountdown("00:00:00");
        // 倒计时结束后，清除缓存
        clearCachedTomorrowEndTime();
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [tomorrowEndTime]);

  return countdown;
};

/**
 * 活动倒计时 Hook
 * @param displayData 活动数据
 * @param onDoubleBetExpire bet加速模式倒计时结束时的回调
 */
export const useActivityCountdown = (
  displayData: BetActivityData,
  onDoubleBetExpire?: () => void
) => {
  const [countdownTime, setCountdownTime] = useState({
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const hasExpiredRef = useRef(false);
  const hasBetActivityExpiredRef = useRef(false);
  // ✅ 使用 ref 保存最新的 endTime，避免因 endTime 变化导致 useEffect 重复执行
  const endTimeRef = useRef(displayData.endTime);
  const doubleBetEndTimeRef = useRef(displayData.doubleBetEndTime);
  const isDoubleBetRef = useRef(displayData.isDoubleBet);

  // ✅ 更新 ref，但不触发 useEffect
  useEffect(() => {
    endTimeRef.current = displayData.endTime;
    doubleBetEndTimeRef.current = displayData.doubleBetEndTime;
    isDoubleBetRef.current = displayData.isDoubleBet;
  }, [
    displayData.endTime,
    displayData.doubleBetEndTime,
    displayData.isDoubleBet,
  ]);

  // ✅ 只在 onDoubleBetExpire 变化时重置过期标志
  useEffect(() => {
    hasExpiredRef.current = false;
    hasBetActivityExpiredRef.current = false;
  }, [onDoubleBetExpire]);

  useEffect(() => {
    const updateCountdown = () => {
      if (isDoubleBetRef.current) {
        const countdown = calculateDoubleBetCountdown(
          doubleBetEndTimeRef.current
        );

        setCountdownTime(countdown);

        // 检查 bet 加速模式是否已结束
        if (
          doubleBetEndTimeRef.current &&
          !hasExpiredRef.current &&
          countdown.hours === "00" &&
          countdown.minutes === "00" &&
          countdown.seconds === "00"
        ) {
          hasExpiredRef.current = true;
          onDoubleBetExpire?.();
        }
      } else {
        // ✅ 使用普通 bet 活动结束时间戳（和bet加速一致的处理方式）
        const countdown = calculateBetActivityCountdown(endTimeRef.current);
        setCountdownTime(countdown);

        // ✅ 检查普通 bet 活动是否已结束
        if (
          endTimeRef.current &&
          !hasBetActivityExpiredRef.current &&
          countdown.hours === "00" &&
          countdown.minutes === "00" &&
          countdown.seconds === "00"
        ) {
          hasBetActivityExpiredRef.current = true;
          onDoubleBetExpire?.();
        }
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [onDoubleBetExpire]);

  return countdownTime;
};

// localStorage 缓存 key 前缀
const BET_AMOUNT_CACHE_KEY_PREFIX = "bet-activity-current-bet-amount";

/**
 * 获取用户特定的缓存 key
 */
const getUserCacheKey = (userId: string | undefined): string => {
  return userId
    ? `${BET_AMOUNT_CACHE_KEY_PREFIX}-${userId}`
    : BET_AMOUNT_CACHE_KEY_PREFIX;
};

/**
 * 获取缓存的 currentBetAmount（用户特定）
 */
const getCachedBetAmount = (userId: string | undefined): number | null => {
  try {
    const cacheKey = getUserCacheKey(userId);
    const cached = localStorage.getItem(cacheKey);
    return cached ? parseFloat(cached) : null;
  } catch {
    return null;
  }
};

/**
 * 设置缓存的 currentBetAmount（用户特定）
 */
const setCachedBetAmount = (
  amount: number,
  userId: string | undefined
): void => {
  try {
    const cacheKey = getUserCacheKey(userId);
    localStorage.setItem(cacheKey, String(amount));
  } catch {
    // ignore
  }
};

// ==================== bet 加速模式结束时间计算（使用缓存）====================

/** bet 加速模式结束时间缓存 key */
const DOUBLE_BET_END_TIME_CACHE_KEY = "bet-activity-double-bet-end-time";
/** bet 加速模式服务器秒数缓存 key（用于检测服务器数据是否变化） */
const DOUBLE_BET_SECONDS_CACHE_KEY = "bet-activity-double-bet-seconds";

/**
 * 获取缓存的 bet 加速结束时间
 */
const getCachedDoubleBetEndTime = (): number | null => {
  try {
    const cached = localStorage.getItem(DOUBLE_BET_END_TIME_CACHE_KEY);
    if (cached) {
      const endTime = parseInt(cached, 10);
      // 如果缓存的结束时间已过期，返回 null
      if (endTime > Date.now()) {
        return endTime;
      }
      // 清除过期缓存
      localStorage.removeItem(DOUBLE_BET_END_TIME_CACHE_KEY);
      localStorage.removeItem(DOUBLE_BET_SECONDS_CACHE_KEY);
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * ✅ 根据服务器秒数创建并缓存 bet 加速结束时间（由 subscribe 调用）
 * 只有在服务器秒数发生变化时才更新缓存，避免倒计时跳动
 */
export const setCachedDoubleBetFromSeconds = (
  balanceWarningExpireSeconds: number
): void => {
  try {
    // 检查服务器秒数是否发生变化
    const cachedSeconds = localStorage.getItem(DOUBLE_BET_SECONDS_CACHE_KEY);
    if (cachedSeconds === String(balanceWarningExpireSeconds)) {
      // 服务器秒数未变化，不更新缓存
      return;
    }

    const endTime = Date.now() + balanceWarningExpireSeconds * 1000;
    localStorage.setItem(DOUBLE_BET_END_TIME_CACHE_KEY, String(endTime));
    localStorage.setItem(
      DOUBLE_BET_SECONDS_CACHE_KEY,
      String(balanceWarningExpireSeconds)
    );
  } catch {
    // ignore
  }
};

/**
 * 清除缓存的 bet 加速结束时间
 */
export const clearCachedDoubleBetEndTime = (): void => {
  try {
    localStorage.removeItem(DOUBLE_BET_END_TIME_CACHE_KEY);
    localStorage.removeItem(DOUBLE_BET_SECONDS_CACHE_KEY);
  } catch {
    // ignore
  }
};

/**
 * ✅ 获取 bet 加速模式结束时间戳（直接返回缓存）
 * 缓存由 useBetStore.subscribe 监听数据变化时自动更新
 * @returns 结束时间戳（毫秒）
 */
export const getDoubleBetEndTimeFromCache = (): number | undefined => {
  const cachedEndTime = getCachedDoubleBetEndTime();
  return cachedEndTime || undefined;
};

// ==================== 普通 Bet 活动结束时间计算（使用缓存）====================

/** 普通 bet 活动结束时间缓存 key */
const BET_ACTIVITY_END_TIME_CACHE_KEY = "bet-activity-end-time";
/** 普通 bet 活动服务器秒数缓存 key（用于检测服务器数据是否变化） */
const BET_ACTIVITY_SECONDS_CACHE_KEY = "bet-activity-seconds";

/**
 * 获取缓存的普通 bet 活动结束时间
 */
const getCachedBetActivityEndTime = (): number | null => {
  try {
    const cached = localStorage.getItem(BET_ACTIVITY_END_TIME_CACHE_KEY);
    if (cached) {
      const endTime = parseInt(cached, 10);
      // 如果缓存的结束时间已过期，返回 null
      if (endTime > Date.now()) {
        return endTime;
      }
      // 清除过期缓存
      localStorage.removeItem(BET_ACTIVITY_END_TIME_CACHE_KEY);
      localStorage.removeItem(BET_ACTIVITY_SECONDS_CACHE_KEY);
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * ✅ 根据服务器秒数创建并缓存结束时间（由 subscribe 调用）
 * 只有在服务器秒数发生变化时才更新缓存，避免倒计时跳动
 */
export const setCachedBetActivityFromSeconds = (
  countdownSeconds: number
): void => {
  try {
    // 检查服务器秒数是否发生变化
    const cachedSeconds = localStorage.getItem(BET_ACTIVITY_SECONDS_CACHE_KEY);
    if (cachedSeconds === String(countdownSeconds)) {
      // 服务器秒数未变化，不更新缓存
      return;
    }

    const endTime = Date.now() + countdownSeconds * 1000;
    localStorage.setItem(BET_ACTIVITY_END_TIME_CACHE_KEY, String(endTime));
    localStorage.setItem(
      BET_ACTIVITY_SECONDS_CACHE_KEY,
      String(countdownSeconds)
    );
  } catch {
    // ignore
  }
};

/**
 * 清除缓存的普通 bet 活动结束时间
 */
export const clearCachedBetActivityEndTime = (): void => {
  try {
    localStorage.removeItem(BET_ACTIVITY_END_TIME_CACHE_KEY);
    localStorage.removeItem(BET_ACTIVITY_SECONDS_CACHE_KEY);
  } catch {
    // ignore
  }
};

/**
 * ✅ 获取普通 bet 活动结束时间戳（直接返回缓存）
 * 缓存由 useBetStore.subscribe 监听数据变化时自动更新
 * @returns 结束时间戳（毫秒）
 */
export const getBetActivityEndTimeFromCache = (): number | undefined => {
  const cachedEndTime = getCachedBetActivityEndTime();
  return cachedEndTime || undefined;
};

// ==================== 明天0点倒计时计算（使用缓存）====================

/** 明天0点倒计时结束时间缓存 key */
const TOMORROW_END_TIME_CACHE_KEY = "bet-activity-tomorrow-end-time";
/** 明天0点倒计时服务器秒数缓存 key（用于检测服务器数据是否变化） */
const TOMORROW_SECONDS_CACHE_KEY = "bet-activity-tomorrow-seconds";

/**
 * 获取缓存的明天0点结束时间
 */
const getCachedTomorrowEndTime = (): number | null => {
  try {
    const cached = localStorage.getItem(TOMORROW_END_TIME_CACHE_KEY);
    if (cached) {
      const endTime = parseInt(cached, 10);
      // 如果缓存的结束时间已过期，返回 null
      if (endTime > Date.now()) {
        return endTime;
      }
      // 清除过期缓存
      localStorage.removeItem(TOMORROW_END_TIME_CACHE_KEY);
      localStorage.removeItem(TOMORROW_SECONDS_CACHE_KEY);
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * ✅ 根据服务器秒数创建并缓存明天0点结束时间（由 subscribe 调用）
 * 只有在服务器秒数发生变化时才更新缓存，避免倒计时跳动
 */
export const setCachedTomorrowFromSeconds = (
  tomorrowCountdownSeconds: number
): void => {
  try {
    // 检查服务器秒数是否发生变化
    const cachedSeconds = localStorage.getItem(TOMORROW_SECONDS_CACHE_KEY);
    if (cachedSeconds === String(tomorrowCountdownSeconds)) {
      // 服务器秒数未变化，不更新缓存
      return;
    }

    const endTime = Date.now() + tomorrowCountdownSeconds * 1000;
    localStorage.setItem(TOMORROW_END_TIME_CACHE_KEY, String(endTime));
    localStorage.setItem(
      TOMORROW_SECONDS_CACHE_KEY,
      String(tomorrowCountdownSeconds)
    );
  } catch {
    // ignore
  }
};

/**
 * 清除缓存的明天0点结束时间
 */
export const clearCachedTomorrowEndTime = (): void => {
  try {
    localStorage.removeItem(TOMORROW_END_TIME_CACHE_KEY);
    localStorage.removeItem(TOMORROW_SECONDS_CACHE_KEY);
  } catch {
    // ignore
  }
};

/**
 * ✅ 获取明天0点结束时间戳（直接返回缓存）
 * 缓存由 useBetStore.subscribe 监听数据变化时自动更新
 * @returns 结束时间戳（毫秒）
 */
export const getTomorrowEndTimeFromCache = (): number | undefined => {
  const cachedEndTime = getCachedTomorrowEndTime();
  return cachedEndTime || undefined;
};

// ==================== 防重复刷新机制 ====================

/** 最后一次刷新倒计时数据的时间戳 key */
const LAST_REFRESH_TIME_KEY = "bet-activity-last-refresh-time";
/** 最小刷新间隔（毫秒）- 5秒内不重复刷新 */
const MIN_REFRESH_INTERVAL = 5000;

/**
 * 检查是否可以刷新（距离上次刷新已超过最小间隔）
 */
const canRefreshNow = (): boolean => {
  try {
    const lastRefreshTime = localStorage.getItem(LAST_REFRESH_TIME_KEY);
    if (!lastRefreshTime) {
      return true;
    }
    const now = Date.now();
    const lastTime = parseInt(lastRefreshTime, 10);
    return now - lastTime >= MIN_REFRESH_INTERVAL;
  } catch {
    return true;
  }
};

/**
 * 标记已刷新
 */
const markRefreshed = (): void => {
  try {
    localStorage.setItem(LAST_REFRESH_TIME_KEY, String(Date.now()));
  } catch {
    // ignore
  }
};

/**
 * ✅ 安全地刷新用户数据（带防重复调用机制）
 * @param loadUserInfo 用户数据刷新函数
 * @returns 是否成功触发刷新
 */
export const safeRefreshUserInfo = async (
  loadUserInfo: () => Promise<any>
): Promise<boolean> => {
  if (!canRefreshNow()) {
    return false;
  }

  markRefreshed();

  try {
    await loadUserInfo();
    return true;
  } catch (error) {
    console.error("[bet] Failed to refresh user info:", error);
    return false;
  }
};

// ==================== Bet 加速弹窗自动弹出记录 ====================

/** bet 加速弹窗当天自动弹出记录 key */
const DOUBLE_BET_AUTO_SHOWN_KEY = "bet-activity-double-bet-auto-shown";

/**
 * 获取今天的日期字符串（用于缓存 key）
 */
const getTodayDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};

/**
 * 检查当天是否已经自动弹出过 bet 加速弹窗
 * @param userId 用户ID
 */
export const hasAutoShownDoubleBetToday = (
  userId: string | undefined
): boolean => {
  try {
    const key = `${DOUBLE_BET_AUTO_SHOWN_KEY}:${userId || "guest"}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      return cached === getTodayDateString();
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * 标记当天已自动弹出过 bet 加速弹窗
 * @param userId 用户ID
 */
export const markDoubleBetAutoShownToday = (
  userId: string | undefined
): void => {
  try {
    const key = `${DOUBLE_BET_AUTO_SHOWN_KEY}:${userId || "guest"}`;
    localStorage.setItem(key, getTodayDateString());
  } catch {
    // ignore
  }
};

// ==================== Bet 任务档位记录（用于判断是否满足新档位）====================
const LAST_TRIGGERED_BET_AMOUNT_KEY = "last_triggered_bet_amount";

/**
 * 获取上次触发弹窗时用户满足的档位金额
 * @param userId 用户ID
 * @param dailyBetTaskId 任务ID
 */
export const getLastTriggeredBetAmount = (
  userId: string | undefined,
  dailyBetTaskId: string | undefined
): number => {
  try {
    const key = `${LAST_TRIGGERED_BET_AMOUNT_KEY}:${userId || "guest"}:${dailyBetTaskId || ""}`;
    const cached = localStorage.getItem(key);
    return cached ? parseFloat(cached) : 0;
  } catch {
    return 0;
  }
};

/**
 * 记录本次触发弹窗时用户满足的档位金额
 * @param userId 用户ID
 * @param dailyBetTaskId 任务ID
 * @param betAmount 档位金额
 */
export const setLastTriggeredBetAmount = (
  userId: string | undefined,
  dailyBetTaskId: string | undefined,
  betAmount: number
): void => {
  try {
    const key = `${LAST_TRIGGERED_BET_AMOUNT_KEY}:${userId || "guest"}:${dailyBetTaskId || ""}`;
    localStorage.setItem(key, betAmount.toString());
  } catch {
    // ignore
  }
};

/**
 * 获取当前满足的最高档位金额（有 day0 可领取奖励的档位）
 * @param dailyBetTask 任务数据
 */
export const getCurrentMaxClaimableBetAmount = (dailyBetTask: any): number => {
  if (!dailyBetTask?.betData) return 0;

  const { taskList, currentBetAmount = 0 } = dailyBetTask.betData;
  if (!taskList?.length) return 0;

  let maxBetAmount = 0;

  // 遍历所有任务，找到有 day0 可领取奖励的最高档位
  taskList.forEach((task: any) => {
    // 只考虑已达到的档位
    if (task.betAmount <= currentBetAmount) {
      // 检查是否有 day0 可领取的奖励
      const hasDay0Claimable = task.releaseRuleItem?.some(
        (rule: any) =>
          rule.day === 0 && rule.canClaim === true && rule.claimed !== true
      );

      if (hasDay0Claimable && task.betAmount > maxBetAmount) {
        maxBetAmount = task.betAmount;
      }
    }
  });

  return maxBetAmount;
};

/**
 * 进度条动画 Hook
 * 只有当 currentBetAmount 对比缓存有变化时才触发动画
 * 如果没有变化，则直接显示最终状态，不播放动画
 * 缓存是基于用户ID的，切换用户时会使用不同的缓存
 * 返回像素值（与卡片位置对齐）
 */
export const useProgressAnimation = (
  displayData: BetActivityData,
  // initialHeight?: number,
  taskFinished?: boolean
) => {
  // 获取当前用户ID
  const userId = useUserStore(state => state.userInfo?.userId);

  // 计算目标高度（rem，与卡片节点对齐）
  const targetHeight = useMemo(() => {
    // 如果任务已完成，返回全部进度条高度（占满 list:after 的 102%）
    if (taskFinished && displayData.items.length > 0) {
      // 计算所有卡片的总高度（list 容器的高度）
      // 每个卡片 = CARD_HEIGHT (3.375rem) + CARD_GAP (0.5rem)
      // 最后一个卡片没有 gap，所以减去一个 CARD_GAP
      const listHeight = displayData.items.length * ITEM_HEIGHT - CARD_GAP;
      // list:after 的高度是 103% 的 list 容器高度
      const afterHeight = listHeight * 1.03;

      // 如果计算出的高度小于等于 22.5rem（容器高度），强制使用 22.95rem（102%）
      const CONTAINER_HEIGHT = 22.5; // 滚动容器高度
      if (afterHeight <= CONTAINER_HEIGHT) {
        return CONTAINER_HEIGHT * 1.02; // 22.95rem = 102% of 22.5rem
      }

      return afterHeight;
    }
    return calculateProgressHeight(
      displayData.currentBetAmount,
      displayData.items
    );
  }, [displayData.currentBetAmount, displayData.items, taskFinished]);

  // 检查是否需要播放动画（对比该用户的缓存）
  const shouldAnimate = useMemo(() => {
    const cachedAmount = getCachedBetAmount(userId);
    const currentAmount = displayData.currentBetAmount;

    // 如果没有缓存，说明是首次加载，需要动画
    if (cachedAmount === null) {
      return true;
    }

    // 如果缓存值和当前值不同，需要动画
    return cachedAmount !== currentAmount;
  }, [displayData.currentBetAmount, userId]);

  // 计算初始状态（如果不需要动画，直接显示最终状态）
  const initialUnlockedNodes = useMemo(() => {
    if (shouldAnimate) {
      return new Set<string>();
    }
    // 不需要动画时，直接解锁所有达到的节点（根据 betAmount 判断）
    const nodes = new Set<string>();
    displayData.items.forEach(item => {
      if (displayData.currentBetAmount >= item.betAmount) {
        nodes.add(item.id);
      }
    });
    return nodes;
  }, [shouldAnimate, displayData.items, displayData.currentBetAmount]);

  const initialVisibleCards = useMemo(() => {
    if (shouldAnimate) {
      return new Set<string>();
    }
    // 不需要动画时，直接显示所有卡片
    return new Set(displayData.items.map(item => item.id));
  }, [shouldAnimate, displayData.items]);

  const [animatedHeight, setAnimatedHeight] = useState(
    shouldAnimate ? 0 : targetHeight
  );
  const [unlockedNodes, setUnlockedNodes] =
    useState<Set<string>>(initialUnlockedNodes);
  const [visibleCards, setVisibleCards] =
    useState<Set<string>>(initialVisibleCards);
  const [isAnimating, setIsAnimating] = useState(false);

  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const hasAnimatedRef = useRef(false);

  // 清理所有定时器
  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  // 当 currentBetAmount 变化时触发动画
  useEffect(() => {
    const currentBetAmount = displayData.currentBetAmount;
    const cachedAmount = getCachedBetAmount(userId);

    // 如果缓存值和当前值相同，不需要动画，直接显示最终状态
    if (cachedAmount !== null && cachedAmount === currentBetAmount) {
      // 确保状态是最终状态
      setAnimatedHeight(targetHeight);
      const allNodes = new Set<string>();
      displayData.items.forEach(item => {
        if (currentBetAmount >= item.betAmount) {
          allNodes.add(item.id);
        }
      });
      setUnlockedNodes(allNodes);
      setVisibleCards(new Set(displayData.items.map(item => item.id)));
      return;
    }

    // 防止重复动画
    if (hasAnimatedRef.current && cachedAmount === currentBetAmount) {
      return;
    }

    hasAnimatedRef.current = true;

    // 清理之前的定时器
    clearAllTimers();
    setIsAnimating(true);

    // 延迟后开始动画
    const startTimer = setTimeout(() => {
      setAnimatedHeight(targetHeight);

      // 计算需要解锁的节点（根据 betAmount 判断）
      const nodesToUnlock: { id: string; delay: number }[] = [];

      displayData.items.forEach((item, index) => {
        const shouldUnlock =
          currentBetAmount >= item.betAmount && !unlockedNodes.has(item.id);
        if (shouldUnlock) {
          // 计算该节点的中心位置（rem）- 与进度条计算使用相同的参考点
          const nodeCenter = getNodeCenter(index);

          // ✅ 计算进度条到达该节点的位置比例（空间上的比例）
          // 如果节点在 targetHeight 之下，正常计算
          // 如果节点在 targetHeight 之上（最后节点且进度超过它），使用节点自己的位置
          let positionRatio: number;
          if (nodeCenter <= targetHeight) {
            // 正常情况：节点在进度条目标高度之下
            positionRatio = targetHeight > 0 ? nodeCenter / targetHeight : 0;
          } else {
            // ✅ 特殊情况：节点在进度条目标高度之上（说明是最后一个节点且进度超过了它）
            // 使用该节点相对于所有节点最大高度的比例，而不是相对于超出的 targetHeight
            const maxNodeCenter = getNodeCenter(displayData.items.length - 1);
            positionRatio = maxNodeCenter > 0 ? nodeCenter / maxNodeCenter : 1;
          }

          // 确保位置比例在 0-1 之间
          const clampedPosition = Math.min(Math.max(positionRatio, 0), 1);

          // ✅ 使用逆向缓动函数计算时间比例
          // 因为进度条使用 cubic-bezier 缓动，到达 50% 位置可能只需要 30% 的时间
          // 到达 90% 位置可能需要 70% 的时间（后面减速）
          const timeRatio = inverseEasing(clampedPosition);

          // ✅ 提前触发解锁动画，让动画更连贯
          // 解锁动画总时长约 0.4s (nodeUnlock) + 0.25s (lockShrink) = 0.65s
          // 提前 200ms 触发，当进度条到达时，动画正好进行到中间的放大阶段
          const baseDelay = timeRatio * PROGRESS_ANIMATION_DURATION;
          const UNLOCK_ADVANCE_TIME = 200; // 提前 200ms
          const delay = Math.max(0, baseDelay - UNLOCK_ADVANCE_TIME);
          nodesToUnlock.push({ id: item.id, delay });
        }
      });

      // 设置解锁定时器
      nodesToUnlock.forEach(({ id, delay }) => {
        const timer = setTimeout(() => {
          setUnlockedNodes(prev => new Set([...prev, id]));
          setVisibleCards(prev => new Set([...prev, id]));
        }, delay);
        timersRef.current.push(timer);
      });

      // 动画完成
      const completeTimer = setTimeout(() => {
        setIsAnimating(false);
        const allCardIds = new Set(displayData.items.map(item => item.id));
        setVisibleCards(allCardIds);
        // 更新该用户的缓存
        setCachedBetAmount(currentBetAmount, userId);
      }, PROGRESS_ANIMATION_DURATION + 300);
      timersRef.current.push(completeTimer);
    }, 200);
    timersRef.current.push(startTimer);

    return () => clearAllTimers();
  }, [
    displayData.currentBetAmount,
    displayData.items,
    userId,
    targetHeight,
    clearAllTimers,
  ]);

  return {
    animatedHeight, // 进度条高度（像素）
    targetHeight, // 目标高度（像素）
    unlockedNodes,
    visibleCards,
    isAnimating,
    shouldAnimate, // 是否需要播放动画（用于节点动画判断）
  };
};
